import { MouseEvent } from "react";
import Image from "next/image";
import { InlineEditableInput } from "@/components/ui/InlineEditableInput";
import { Folder } from "../types";
import { buildAssetUrl } from "../utils";

type FolderListProps = {
  folders: Folder[];
  inlineFolderName: string | null;
  onInlineNameChange: (value: string) => void;
  onInlineCreate: (name: string) => void;
  onInlineCancel: () => void;
  selectedFolderIds: string[];
  onFolderClick: (id: string) => void;
  onFolderContextMenu: (id: string, event: MouseEvent) => void;
  onToggleFolderSelection: (id: string, checked: boolean) => void;
  onRenameFolder: (id: string, name: string) => void;
  renameFolderId: string | null;
  showCheckbox?: boolean;
  enableContextMenu?: boolean;
};

export function FolderList({
  folders,
  inlineFolderName,
  onInlineNameChange,
  onInlineCreate,
  onInlineCancel,
  selectedFolderIds,
  onFolderClick,
  onFolderContextMenu,
  onToggleFolderSelection,
  onRenameFolder,
  renameFolderId,
  showCheckbox = true,
  enableContextMenu = true,
}: FolderListProps) {
  return (
    <div className="space-y-0">
      {inlineFolderName !== null ? (
        <div className="bg-pinkCustom/10 grid grid-cols-4 items-center px-4 py-2 text-sm">
          <div className="col-span-2 flex items-center gap-2">
            <span>📁</span>
            <input
              placeholder="New Folder"
              autoFocus
              className="w-full max-w-32 rounded border px-2 py-1 text-xs focus:border-gray-300 focus:bg-white focus:outline-none"
              value={inlineFolderName}
              onChange={(e) => onInlineNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onInlineCreate(inlineFolderName);
                }
                if (e.key === "Escape") {
                  onInlineCancel();
                }
              }}
              onBlur={() => onInlineCreate(inlineFolderName)}
            />
          </div>
          <div className="text-xs text-gray-600">Folder</div>
          <div className="text-right text-xs text-gray-600">—</div>
        </div>
      ) : null}

      {folders.map((folder) => {
        const isSelected = selectedFolderIds.includes(folder.id);
        const isRenaming = renameFolderId === folder.id;
        return (
          <div
            key={folder.id}
            className={`hover:bg-pinkCustom/5 grid cursor-pointer grid-cols-4 items-center px-4 py-2 text-sm ${
              isSelected ? "bg-pinkCustom/15" : ""
            }`}
            onClick={() => onFolderClick(folder.id)}
            onContextMenu={(e) => {
              if (!enableContextMenu) return;
              e.preventDefault();
              onFolderContextMenu(folder.id, e);
            }}
          >
            <div className="col-span-2 flex items-center gap-2">
              {showCheckbox ? (
                <input
                  type="checkbox"
                  className="accent-pinkCustom h-4 w-4"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleFolderSelection(folder.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : null}
              {folder.previewAsset ? (
                <Image
                  src={buildAssetUrl(folder.previewAsset)}
                  alt={`${folder.name} product hero`}
                  width={28}
                  height={28}
                  className="h-7 w-7 rounded object-cover"
                  unoptimized
                />
              ) : (
                <span>📁</span>
              )}
              <div className="flex-1 truncate">
                <InlineEditableInput
                  value={folder.name}
                  onSave={(next) => onRenameFolder(folder.id, next)}
                  className="truncate"
                  startEditing={isRenaming}
                  enableClickToEdit={false}
                />
              </div>
            </div>
            <div className="text-xs text-gray-600">Folder</div>
            <div className="text-right text-xs text-gray-600">—</div>
          </div>
        );
      })}
    </div>
  );
}
